'use client';

import Image from 'next/image';
import { reduxLogger } from '@/core/utils/logger';
import Link from 'next/link';
import { useState, useRef } from 'react';
import { Loader2, ArrowLeft } from 'lucide-react';
import { FormInput, PasswordInput } from '@/shared/components/ui/FormInput';
import { logos } from '@/shared/assets/images';
import { useRouter } from 'next/navigation';
import { useZodForm, signupSchema, type SignupFormData } from '@/shared/validation';
import { useSignupAgencyOwnerMutation } from '@/api/features/users';
import { useLoginMutation, useValidateSessionMutation } from '@/api/features/auth';
import { baseApi } from '@/api/core/baseApi';
import { useAppDispatch } from '@/core/store/hooks';
import { useOnboardingNavigation } from '../hooks/useOnboardingNavigation';
import { STEP_TO_ROUTE_MAP } from '../hooks/useOnboardingSession';
import { COMPANY } from '@voley/shared-types/constants';
import { safeSetItem } from '@/shared/utils/safeStorage';
import type { LoginResult, ApiError, ApiErrorData, SignupResult } from '@/features/onboarding/types';
import { extractErrorMessage } from '@/features/onboarding/types';
import { useOtpFlow } from '@/features/onboarding/hooks/useOtpFlow';
import { BRAND_BLUE } from '@voley/shared-types/constants';
import { getErrorMessage } from '@voley/shared-utils';
import { syncAuthState } from '@/core/auth/utils/syncAuthState';
import { TimeoutError } from '@voley/shared-utils/with-timeout';

type CallWithTimeoutOptions = {
  onTimeout?: () => void; // called if the timeout fires to allow cooperative cancellation
};

// Simple typed shape for RTK Query mutation trigger results we interact with.
type RTKTrigger<T> = {
  unwrap: () => Promise<T>;
  abort?: () => void;
};

export function SignupContainer() {
  // No CSRF needed for initial signup
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { navigateWithLoading, isNavigating } = useOnboardingNavigation();
  const brandBlue = BRAND_BLUE;

  // Track if we're in the post-signup transition phase
  const [isTransitioning, setIsTransitioning] = useState(false);

  // OTP email verification flow (email → OTP → details)
  const {
    signupStep,
    verifiedEmail,
    emailInput,
    setEmailInput,
    emailError,
    handleSendOtp,
    isSendingOtp,
    otpCode,
    otpError,
    otpInputRefs,
    handleOtpChange,
    handleOtpPaste,
    handleOtpKeyDown,
    handleVerifyOtp,
    handleResendOtp,
    handleBackToEmail,
    isVerifyingOtp,
  } = useOtpFlow();

  // Use fixedCacheKey to prevent cancellation on component unmount
  const [signupAgencyOwner, { isLoading, error }] = useSignupAgencyOwnerMutation({
    fixedCacheKey: 'signup-agency-owner-fixed'
  });
  const [loginMutation] = useLoginMutation({
    fixedCacheKey: 'login-mutation-fixed'
  });
  const [validateSession] = useValidateSessionMutation({
    fixedCacheKey: 'validate-session-fixed'
  });
  
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useZodForm(signupSchema);

  // Utility: add a client-side safety timeout to avoid infinite pending UI
  // Supports an optional cooperative cancellation hook so callers can attempt to abort
  // the underlying request (when the request API exposes an abort method).
  const callWithTimeout = async <T,>(
    promise: Promise<T>,
    ms: number,
    label: string,
    options?: CallWithTimeoutOptions
  ): Promise<T> => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
          try {
            options?.onTimeout?.();
          } catch {
            // swallow errors from onTimeout to ensure timeout rejection still happens
          }
          reject(new TimeoutError(`${label} timed out after ${ms}ms`));
        }, ms
      );
    });
    try {
      const result = await Promise.race([promise, timeout]);
      return result as T;
    } finally {
      if (timer) clearTimeout(timer);
    }
  };

  // Track submission tokens so fallback logic doesn't run for stale submissions
  const submissionIdRef = useRef(0);
  const timedOutSubmissions = useRef<Record<number, boolean>>({});

  const onSubmit: (data: SignupFormData) => Promise<void> = async (data) => {
    const submissionId = ++submissionIdRef.current;
    try {
      // Use the verified email from OTP flow instead of form input
      const signupData = {
        email: verifiedEmail,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        organizationName: data.companyName,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      
      reduxLogger.info('[Signup] Sending signup data');
      const signupOp = signupAgencyOwner({ data: signupData }) as RTKTrigger<SignupResult>;
      const result = await callWithTimeout(
        signupOp.unwrap(),
        45000, // Match the timeout configured in timeoutConfig.ts for signupAgencyOwner
        'Signup request',
        { onTimeout: () => { timedOutSubmissions.current[submissionId] = true; signupOp.abort?.(); } }
      );

      reduxLogger.info('[Signup] Signup successful');

      if (result?.user) {
        safeSetItem('onboarding_user_id', result.user.id);
        if (result.stytchUserId) safeSetItem('stytch_user_id', result.stytchUserId);
      }

      // Now log in the user with their credentials using the RTK Query mutation
      try {
        reduxLogger.info('[Signup] Attempting login after signup');
        setIsTransitioning(true); // Start transition state for visual feedback
        
        const loginOp = loginMutation({ email: verifiedEmail, password: data.password, clientType: 'web' }) as RTKTrigger<unknown>;
        const loginResult = await callWithTimeout<unknown>(
          loginOp.unwrap(),
          15000,
          'Post-signup login',
          { onTimeout: () => { timedOutSubmissions.current[submissionId] = true; loginOp.abort?.(); } }
        );

        reduxLogger.info('Post-signup login result:', loginResult);

        // Sync auth state so SecureAuthProvider knows we're authenticated.
        // Without this, DashboardBootstrap gets stuck on client-side navigation.
        syncAuthState((loginResult as LoginResult)?.user ?? undefined);

        // CRITICAL: Invalidate UserProfile cache to ensure fresh data on next page
        // Without this, stale cache causes stuck loading on company-info page
        dispatch(baseApi.util.invalidateTags(['UserProfile', 'OnboardingSession']));

        // Reduced delay - cookies are set immediately, just need minimal time for browser to process
        await new Promise(resolve => setTimeout(resolve, 150));

        // Proactively validate session to avoid middleware race conditions
        try {
          reduxLogger.info('[Signup] Validating session after login');
          const validateOp = validateSession() as RTKTrigger<unknown>;
          await callWithTimeout<unknown>(validateOp.unwrap(), 6000, 'Session validate', { onTimeout: () => validateOp.abort?.() });
        } catch (e) {
          reduxLogger.warn('Session validation after signup-login failed; proceeding with hint cookie trust', e);
        }

        const currentStep: string | undefined = (loginResult as LoginResult)?.onboardingSession?.currentStep;
        const nextRoute = currentStep && STEP_TO_ROUTE_MAP[currentStep] ? STEP_TO_ROUTE_MAP[currentStep] : '/company-info';

        // CRITICAL: Double-invalidate cache right before navigation to ensure fresh data
        // This is defensive - the first invalidation after login may not be fully processed
        dispatch(baseApi.util.invalidateTags(['UserProfile', 'OnboardingSession']));
        await new Promise(resolve => setTimeout(resolve, 100));

        // Use the navigation hook for smooth transition with loading overlay
        await navigateWithLoading(nextRoute);

      } catch (authError) {
        reduxLogger.error('[Signup] Failed to authenticate after signup:', authError);
        setIsTransitioning(false);
        router.replace('/login?message=account_created&returnUrl=%2Fcompany-info');
      }

    } catch (error) {
      reduxLogger.error('[Signup] Signup failed:', error);
      const errObj = error as ApiError;
      const errData = errObj?.data;
      const errMsg = errData?.error || errData?.message || errObj?.message || '';
      reduxLogger.error('[Signup] Signup error details:', {
        fullError: error,
        data: errData,
        status: errObj?.status,
        message: errMsg,
      });

      const isNetworkAbort = (
        errObj?.status === 0 ||
        errObj?.status === 'FETCH_ERROR' ||
        /AbortError/i.test(String(errObj?.name)) ||
        /timeout/i.test(String(errMsg)) ||
        /NetworkError/i.test(String(errMsg))
      );

      // Helper to extract step from login result
      const getNextRouteFromLogin = (loginResult: unknown): string => {
        const result = loginResult as LoginResult;
        const currentStep = result?.onboardingSession?.currentStep;
        return currentStep && STEP_TO_ROUTE_MAP[currentStep] ? STEP_TO_ROUTE_MAP[currentStep] : '/company-info';
      };

      if (isNetworkAbort) {
        // Only attempt fallback login if this submission was marked timed-out/aborted
        if (!timedOutSubmissions.current[submissionId]) {
          reduxLogger.warn('[Signup] Network/abort detected but submission not marked timed-out; skipping fallback login');
        } else {
          try {
            reduxLogger.warn('[Signup] Network/abort detected, attempting login fallback');
            setIsTransitioning(true);
            const loginOp = loginMutation({ email: verifiedEmail, password: data.password, clientType: 'web' }) as RTKTrigger<unknown>;
            const loginResult = await callWithTimeout<unknown>(loginOp.unwrap(), 15000, 'Login after abort', { onTimeout: () => loginOp.abort?.() });
            syncAuthState((loginResult as LoginResult)?.user ?? undefined);
            // Invalidate cache for fresh data on next page
            dispatch(baseApi.util.invalidateTags(['UserProfile', 'OnboardingSession']));
            await new Promise(resolve => setTimeout(resolve, 150));
            try {
              const validateOp = validateSession() as RTKTrigger<unknown>;
              await callWithTimeout<unknown>(validateOp.unwrap(), 6000, 'Session validate after abort', { onTimeout: () => validateOp.abort?.() });
            } catch { /* ignore validation errors */ }
            const nextRoute = getNextRouteFromLogin(loginResult);
            await navigateWithLoading(nextRoute);
            return;
          } catch (loginErr) {
            reduxLogger.error('[Signup] Fallback login after abort failed', loginErr);
            setIsTransitioning(false);
            router.replace('/login?message=account_created&returnUrl=%2Fcompany-info');
            return;
          }
        }
      }

      // Check for duplicate email / existing account errors
      const isDuplicateEmail = (
        errObj?.status === 400 ||
        errObj?.status === 404 ||
        (typeof errMsg === 'string' && /endpoint not found/i.test(errMsg)) ||
        (typeof errMsg === 'string' && /already exists/i.test(errMsg)) ||
        (typeof errMsg === 'string' && /duplicate/i.test(errMsg)) ||
        errData?.errorType === 'DUPLICATE_EMAIL'
      );

      if (isDuplicateEmail) {
        try {
          reduxLogger.info('[Signup] Detected existing account (duplicate email), attempting login');
          setIsTransitioning(true);
          const loginOp = loginMutation({ email: verifiedEmail, password: data.password, clientType: 'web' }) as RTKTrigger<unknown>;
          const loginResult = await callWithTimeout<unknown>(loginOp.unwrap(), 15000, 'Login after existing-account', { onTimeout: () => loginOp.abort?.() });
          syncAuthState((loginResult as LoginResult)?.user ?? undefined);
          // Invalidate cache for fresh data on next page
          dispatch(baseApi.util.invalidateTags(['UserProfile', 'OnboardingSession']));
          // Reduced delay - cookies are set immediately
          await new Promise(resolve => setTimeout(resolve, 150));
          try {
            const validateOp = validateSession() as RTKTrigger<unknown>;
            await callWithTimeout<unknown>(validateOp.unwrap(), 6000, 'Session validate after existing-account', { onTimeout: () => validateOp.abort?.() });
          } catch { /* ignore validation errors */ }
          const nextRoute = getNextRouteFromLogin(loginResult);
          reduxLogger.info('[Signup] Redirecting to:', nextRoute);
          await navigateWithLoading(nextRoute);
          return;
        } catch (loginErr) {
          reduxLogger.error('[Signup] Fallback login after existing-account failed', loginErr);
          setIsTransitioning(false);
          // The error state is already displayed by the error component
          return;
        }
      }
      // Handle error (show toast, etc.)
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: 'var(--color-card-background)' }}
    >
      {/* Subtle gradient background */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at top right, rgba(53, 129, 207, 0.04) 0%, transparent 50%), radial-gradient(ellipse at bottom left, rgba(53, 129, 207, 0.03) 0%, transparent 50%)',
        }}
      />

      {/* Main content container */}
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-sm border border-gray-100 relative z-10 p-5 sm:p-8">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <Link href="/home">
            <Image
              src={logos.ballOnly}
              alt="Voley"
              width={52}
              height={52}
              className="h-12 w-12 md:hidden"
              priority
            />
            <Image
              src={logos.voleyT}
              alt="Voley"
              width={120}
              height={28}
              className="hidden md:inline h-8 w-auto"
              priority
            />
          </Link>
        </div>

        {/* Header - changes based on step */}
        <div className="mb-6 text-center">
          {signupStep === 'email' && (
            <>
              <h2 className="text-2xl font-semibold tracking-tight text-text-primary">
                Create your account
              </h2>
              <p className="mt-2 text-sm text-text-tertiary">
                Start your 30-day free trial
              </p>
            </>
          )}
          {signupStep === 'otp' && (
            <>
              <h2 className="text-2xl font-semibold tracking-tight text-text-primary">
                Verify your email
              </h2>
              <p className="mt-2 text-sm text-text-tertiary">
                We sent a 6-digit code to <span className="font-medium">{verifiedEmail}</span>
              </p>
            </>
          )}
          {signupStep === 'details' && (
            <>
              <h2 className="text-2xl font-semibold tracking-tight text-text-primary">
                Complete your registration
              </h2>
              <p className="mt-2 text-sm text-text-tertiary">
                Email verified: <span className="font-medium">{verifiedEmail}</span>
              </p>
            </>
          )}
        </div>

        {signupStep === 'email' && (
          <SignupEmailStep emailInput={emailInput} setEmailInput={setEmailInput}
            emailError={emailError} handleSendOtp={handleSendOtp} isSendingOtp={isSendingOtp} brandBlue={brandBlue} />
        )}
        {signupStep === 'otp' && (
          <SignupOtpStep otpCode={otpCode} otpError={otpError} otpInputRefs={otpInputRefs}
            handleOtpChange={handleOtpChange} handleOtpPaste={handleOtpPaste} handleOtpKeyDown={handleOtpKeyDown}
            handleVerifyOtp={handleVerifyOtp} handleResendOtp={handleResendOtp} handleBackToEmail={handleBackToEmail}
            isVerifyingOtp={isVerifyingOtp} isSendingOtp={isSendingOtp} brandBlue={brandBlue} />
        )}
        {signupStep === 'details' && (
          <SignupDetailsStep
            error={error} verifiedEmail={verifiedEmail} register={register}
            errors={errors} handleSubmit={handleSubmit} onSubmit={onSubmit}
            isLoading={isLoading} isTransitioning={isTransitioning} isNavigating={isNavigating}
            brandBlue={brandBlue}
          />
        )}
        </div>

      {/* Footer */}
      <div className="mt-8 text-center relative z-10">
        <p className="text-sm text-gray-400">
          {COMPANY.copyrightNotice}
        </p>
      </div>
    </div>
  );
}

// --- Shared error alert for signup steps ---
function SignupErrorAlert({ message, children }: { message: string; children?: React.ReactNode }) {
  return (
    <div className="rounded-md bg-red-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-red-700">{message}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

// --- Sub-component: Step 1 - Email Entry ---
function SignupEmailStep({
  emailInput, setEmailInput, emailError, handleSendOtp, isSendingOtp, brandBlue,
}: {
  emailInput: string; setEmailInput: (v: string) => void; emailError: string | null;
  handleSendOtp: () => void; isSendingOtp: boolean; brandBlue: string;
}) {
  return (
    <div className="space-y-5">
      {emailError && (
        <SignupErrorAlert message={emailError}>
          {emailError.includes('already exists') && (
            <div className="mt-2 text-sm">
              <p className="text-gray-600 mb-1">If you were interrupted during signup, logging in will resume where you left off.</p>
              <Link href="/login" className="font-medium text-blue-600 hover:text-blue-500">Log in to continue &rarr;</Link>
            </div>
          )}
        </SignupErrorAlert>
      )}
      <div>
        <label htmlFor="emailInput" className="block text-sm font-medium mb-2 text-text-primary">Work Email</label>
        <input id="emailInput" type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSendOtp(); } }}
          className="w-full px-4 py-3 rounded-xl text-base transition-all duration-200"
          style={{ backgroundColor: 'var(--color-card-background-header)', border: emailError ? '1px solid var(--color-error)' : '1px solid var(--color-border)', color: 'var(--color-text-primary)', outline: 'none' }}
          onFocus={(e) => { e.target.style.borderColor = brandBlue; e.target.style.boxShadow = `0 0 0 3px ${brandBlue}20`; }}
          onBlur={(e) => { e.target.style.borderColor = emailError ? 'var(--color-error)' : 'var(--color-border)'; e.target.style.boxShadow = 'none'; }}
          placeholder="Enter your work email" autoFocus />
      </div>
      <div className="pt-2">
        <button type="button" onClick={handleSendOtp} disabled={isSendingOtp}
          className="w-full py-3 px-4 rounded-full text-base font-medium text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{ backgroundColor: brandBlue, boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)' }}>
          {isSendingOtp && <Loader2 className="h-5 w-5 animate-spin" />}
          {isSendingOtp ? 'Sending code...' : 'Continue'}
        </button>
      </div>
      <div className="text-center pt-2">
        <p className="text-sm text-text-tertiary">
          Already have an account?{' '}<Link href="/login" className="font-medium hover:underline" style={{ color: brandBlue }}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}

// --- Sub-component: Step 2 - OTP Verification ---
function SignupOtpStep({
  otpCode, otpError, otpInputRefs, handleOtpChange, handleOtpPaste, handleOtpKeyDown,
  handleVerifyOtp, handleResendOtp, handleBackToEmail, isVerifyingOtp, isSendingOtp, brandBlue,
}: {
  otpCode: string[]; otpError: string | null; otpInputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
  handleOtpChange: (i: number, v: string) => void; handleOtpPaste: (e: React.ClipboardEvent) => void;
  handleOtpKeyDown: (i: number, e: React.KeyboardEvent) => void; handleVerifyOtp: () => void;
  handleResendOtp: () => void; handleBackToEmail: () => void;
  isVerifyingOtp: boolean; isSendingOtp: boolean; brandBlue: string;
}) {
  return (
    <div className="space-y-5">
      <button type="button" onClick={handleBackToEmail} className="flex items-center gap-2 text-sm font-medium transition-colors text-text-tertiary">
        <ArrowLeft className="h-4 w-4" />Change email
      </button>
      {otpError && <SignupErrorAlert message={otpError} />}
      <div>
        <label className="block text-sm font-medium mb-3 text-center text-text-primary">Enter verification code</label>
        <div className="flex justify-center gap-1.5 sm:gap-2" onPaste={handleOtpPaste}>
          {otpCode.map((digit, index) => (
            <input key={index} ref={(el) => { otpInputRefs.current[index] = el; }} type="text" inputMode="numeric" maxLength={1}
              value={digit} onChange={(e) => handleOtpChange(index, e.target.value)} onKeyDown={(e) => handleOtpKeyDown(index, e)}
              className="w-10 h-12 sm:w-12 sm:h-14 text-center text-lg sm:text-xl font-semibold rounded-xl transition-all duration-200"
              style={{ backgroundColor: 'var(--color-card-background-header)', border: otpError ? '1px solid var(--color-error)' : '1px solid var(--color-border)', color: 'var(--color-text-primary)', outline: 'none' }}
              onFocus={(e) => { e.target.style.borderColor = brandBlue; e.target.style.boxShadow = `0 0 0 3px ${brandBlue}20`; }}
              onBlur={(e) => { e.target.style.borderColor = otpError ? 'var(--color-error)' : 'var(--color-border)'; e.target.style.boxShadow = 'none'; }} />
          ))}
        </div>
      </div>
      <div className="pt-2">
        <button type="button" onClick={handleVerifyOtp} disabled={isVerifyingOtp || otpCode.join('').length !== 6}
          className="w-full py-3 px-4 rounded-full text-base font-medium text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{ backgroundColor: brandBlue, boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)' }}>
          {isVerifyingOtp && <Loader2 className="h-5 w-5 animate-spin" />}
          {isVerifyingOtp ? 'Verifying...' : 'Verify'}
        </button>
      </div>
      <div className="text-center pt-2">
        <p className="text-sm text-text-tertiary">
          Didn&apos;t receive the code?{' '}
          <button type="button" onClick={handleResendOtp} disabled={isSendingOtp} className="font-medium hover:underline disabled:opacity-50" style={{ color: brandBlue }}>
            {isSendingOtp ? 'Sending...' : 'Resend code'}
          </button>
        </p>
      </div>
    </div>
  );
}

// --- Sub-component: Step 3 - Account Details Form ---
function SignupDetailsStep({
  error, verifiedEmail, register, errors, handleSubmit, onSubmit,
  isLoading, isTransitioning, isNavigating, brandBlue,
}: {
  error: unknown; verifiedEmail: string;
  register: ReturnType<typeof useZodForm<typeof signupSchema>>['register'];
  errors: ReturnType<typeof useZodForm<typeof signupSchema>>['formState']['errors'];
  handleSubmit: ReturnType<typeof useZodForm<typeof signupSchema>>['handleSubmit'];
  onSubmit: (data: SignupFormData) => Promise<void>;
  isLoading: boolean; isTransitioning: boolean; isNavigating: boolean; brandBlue: string;
}) {
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {error && typeof error === 'object' && error !== null ? (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Signup Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  {(() => {
                    if ('data' in (error as object)) {
                      const errorData = (error as { data: unknown }).data as ApiErrorData;
                      return extractErrorMessage(errorData, 'An error occurred during signup. Please try again.');
                    }
                    return getErrorMessage(error) || 'An error occurred during signup. Please try again.';
                  })()}
                </p>
                {(() => {
                  const errorMsg = 'data' in (error as object)
                    ? extractErrorMessage((error as { data: unknown }).data as ApiErrorData, '')
                    : getErrorMessage(error);
                  return errorMsg.toLowerCase().includes('already exists') && (
                    <div className="mt-2">
                      <p className="text-gray-600 mb-1">If you were interrupted during signup, logging in will resume where you left off.</p>
                      <Link href="/login" className="font-medium text-blue-600 hover:text-blue-500">Log in to continue &rarr;</Link>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <FormInput id="companyName" label="Company/Agency Name" placeholder="Enter your company or agency name" error={errors.companyName?.message} {...register('companyName')} />
      <input type="hidden" {...register('workEmail')} value={verifiedEmail} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
        <FormInput id="firstName" label="First Name" placeholder="First name" error={errors.firstName?.message} {...register('firstName')} />
        <FormInput id="lastName" label="Last Name" placeholder="Last name" error={errors.lastName?.message} {...register('lastName')} />
      </div>
      <PasswordInput id="password" label="Your Password" placeholder="Create a strong password" error={errors.password?.message} hint="Must be at least 10 characters" {...register('password')} />
      <PasswordInput id="confirmPassword" label="Confirm Password" placeholder="Confirm your password" error={errors.confirmPassword?.message} {...register('confirmPassword')} />
      <div className="pt-2">
        <button type="submit" disabled={isLoading || isTransitioning || isNavigating}
          className="w-full py-3 px-4 rounded-full text-base font-medium text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{ backgroundColor: brandBlue, boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)' }}
          onMouseEnter={(e) => { if (!isLoading && !isTransitioning && !isNavigating) { e.currentTarget.style.transform = 'scale(1.01)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgb(0 0 0 / 0.1)'; } }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 1px 2px 0 rgb(0 0 0 / 0.05)'; }}>
          {(isLoading || isTransitioning || isNavigating) && <Loader2 className="h-5 w-5 animate-spin" />}
          {isTransitioning || isNavigating ? 'Setting up your account...' : isLoading ? 'Creating account...' : 'Create Account'}
        </button>
      </div>
    </form>
  );
}
